import React, { useState, useRef } from 'react';
import { invitationPendingCount, sendAllPendingInvitations } from '../utils/invitationRow';
import DeactivatedInviteesNotice from '../components/DeactivatedInviteesNotice';
import {
  AlertTitle,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
  Box,
  Heading,
  Button,
  HStack,
  VStack,
  Text,
  Badge,
  Card,
  CardBody,
  CardHeader,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Flex,
  Spacer,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  IconButton,
  useToast,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Input,
  InputGroup,
  InputLeftElement,
  Select,
  Spinner,
  Center,
  Alert,
  AlertIcon,
  SimpleGrid,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  Progress,
  AlertDescription,
  Tooltip,
  Checkbox,
  Menu,
  MenuButton,
  MenuDivider,
  MenuList,
  MenuItem,
  FormControl,
  FormLabel,
  Textarea,
  RadioGroup,
  Radio,
  Stack,
  Divider,
  useBreakpointValue,
} from '@chakra-ui/react';
import {
  EditIcon,
  DeleteIcon,
  AddIcon,
  SearchIcon,
  InfoIcon,
  ChevronDownIcon,
  DownloadIcon,
  EmailIcon,
  RepeatIcon,
  CheckIcon,
  WarningIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
} from '@chakra-ui/icons';
import { useNavigate, useParams } from 'react-router-dom';
import EmailsTab from '../components/emails/EmailsTab';
import SectionCard from '../components/events/SectionCard';
import EventTeamSection from '../components/events/EventTeamSection';
import { useAuth } from '../contexts/AuthContext';
import { isFrontDesk } from '../utils/access';
import SendEmailModal, { SystemEmailAction } from '../components/emails/SendEmailModal';
import SendEmailsTab from '../components/emails/SendEmailsTab';
import CheckinTab from '../components/events/CheckinTab';
import { EVENT_TAB_LABELS, defaultEventTabIndexFor, eventTabsFor } from '../components/events/eventTabs';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiService } from '../services/api';
import { Contact, InviteeWithDetails } from '../types/api';
import { formatDateAdmin } from '../utils/dateUtils';


interface EventStats {
  total: number;
  responded: number;
  accepted: number;
  declined: number;
  maybe: number;
  noResponse: number;
  checkedIn: number;
  responseRate: number;
  vips: number;
  needsReinvitation: number;
}

// ============================================
// HELPER FUNCTIONS FOR RSVP STATUS
// ============================================
const getResponseStatusBadge = (status: string) => {
  switch (status) {
    case 'accepted':
      return { colorScheme: 'green', icon: '✓', label: 'Présent' };
    case 'declined':
      return { colorScheme: 'red', icon: '✗', label: 'Absent' };
    case 'maybe':
      return { colorScheme: 'yellow', icon: '?', label: 'Peut-être' };
    case 'pending':
      return { colorScheme: 'gray', icon: '⏳', label: 'En attente' };
    case 'no_response':
      return { colorScheme: 'gray', icon: '—', label: 'Sans réponse' };
    default:
      return { colorScheme: 'gray', icon: '—', label: status };
  }
};

const getVIPStatusColor = (status: string) => {
  switch (status) {
    case 'very_vip': return 'purple';
    case 'board_member': return 'red';
    case 'vip': return 'blue';
    case 'sponsor': return 'green';
    case 'media': return 'orange';
    default: return 'gray';
  }
};

const getVIPStatusLabel = (status: string) => {
  switch (status) {
    case 'very_vip': return 'VIP+';
    case 'board_member': return 'Conseil';
    case 'vip': return 'VIP';
    case 'sponsor': return 'Sponsor';
    case 'media': return 'Média';
    case 'none': return '-';
    default: return status;
  }
};

// ============================================
// INVITEE CARD COMPONENT (Mobile View)
// ============================================
interface InviteeCardProps {
  invitee: InviteeWithDetails;
  onRemove: () => void;
  onSendInvitation: () => void;
  onOpenManualRSVP: (status: string) => void;
  onResendConfirmation: () => void;
  isSelected?: boolean;
  onToggleSelect?: () => void;
  sendInvitationLoading?: boolean;
  resendConfirmationLoading?: boolean;
}

const InviteeCard: React.FC<InviteeCardProps> = ({
  invitee,
  onRemove,
  onSendInvitation,
  onOpenManualRSVP,
  onResendConfirmation,
  isSelected,
  onToggleSelect,
  sendInvitationLoading,
  resendConfirmationLoading,
}) => {
  const responseBadge = getResponseStatusBadge(invitee.response_status || 'pending');

  return (
    <Card
      bg={isSelected ? 'blue.50' : 'white'}
      borderWidth={isSelected ? '2px' : '1px'}
      borderColor={isSelected ? 'blue.500' : 'gray.200'}
    >
      <CardBody>
        <VStack align="stretch" spacing={3}>
          {/* Header Row */}
          <Flex justify="space-between" align="start">
            <VStack align="start" spacing={1} flex="1">
              <HStack>
                {onToggleSelect && (
                  <Checkbox
                    isChecked={isSelected}
                    onChange={onToggleSelect}
                  />
                )}
                <Text fontWeight="bold" fontSize="md">
                  {invitee.contact.full_name}
                </Text>
              </HStack>
              
              {invitee.contact.organization && (
                <Text fontSize="sm" color="gray.600">
                  {invitee.contact.organization}
                </Text>
              )}
              
              <Text fontSize="xs" color="gray.500">
                {invitee.contact.email || 'Pas d\'email'}
              </Text>
            </VStack>

            {/* VIP Badge */}
            {invitee.effective_vip_status !== 'none' && (
              <Badge colorScheme={getVIPStatusColor(invitee.effective_vip_status)}>
                {getVIPStatusLabel(invitee.effective_vip_status)}
              </Badge>
            )}
          </Flex>

          <Divider />

          {/* Status Row */}
          <SimpleGrid columns={2} spacing={3}>
            <Box>
              <Text fontSize="xs" color="gray.500" mb={1}>
                Invitation
              </Text>
              <VStack align="start" spacing={1}>
                <Badge
                  colorScheme={
                    invitee.invitation_status === 'sent' ? 'green' :
                    invitee.invitation_status === 'pending' ? 'yellow' :
                    'gray'
                  }
                  fontSize="xs"
                >
                  {invitee.invitation_status === 'sent' ? 'Envoyée' :
                   invitee.invitation_status === 'pending' ? 'En attente' :
                   invitee.invitation_status}
                </Badge>
                {invitee.needs_reinvitation && (
                  <Badge colorScheme="orange" fontSize="xs">
                    À renvoyer
                  </Badge>
                )}
                {invitee.email_has_changed && (
                  <Badge colorScheme="red" fontSize="xs">
                    Email modifié
                  </Badge>
                )}
              </VStack>
              {invitee.last_invitation_sent_at && (
                <Text fontSize="xs" color="gray.500" mt={1}>
                  {new Date(invitee.last_invitation_sent_at).toLocaleDateString('fr-FR')}
                </Text>
              )}
            </Box>

            <Box>
              <Text fontSize="xs" color="gray.500" mb={1}>
                Réponse
              </Text>
              <Badge colorScheme={responseBadge.colorScheme} fontSize="xs">
                {responseBadge.icon} {responseBadge.label}
              </Badge>
              {invitee.response_date && (
                <Text fontSize="xs" color="gray.500" mt={1}>
                  {new Date(invitee.response_date).toLocaleDateString('fr-FR')}
                </Text>
              )}
            </Box>
          </SimpleGrid>

          {/* Action Buttons */}
          <HStack spacing={2} justify="stretch">
            {invitee.contact.email && (
              <Button
                size="sm"
                colorScheme="blue"
                leftIcon={<EmailIcon />}
                onClick={onSendInvitation}
                flex="1"
                isLoading={sendInvitationLoading}
              >
                {invitee.invitation_status === 'pending' ? 'Envoyer' : 'Renvoyer'}
              </Button>
            )}
            
            <Menu>
              <MenuButton
                as={IconButton}
                icon={<ChevronDownIcon />}
                size="sm"
                variant="outline"
                aria-label="Actions"
              />
              <MenuList>
                <MenuItem 
                  icon={<CheckIcon color="green.500" />} 
                  onClick={() => onOpenManualRSVP('accepted')}
                >
                  Marquer « Présent »
                </MenuItem>
                <MenuItem 
                  icon={<WarningIcon color="orange.500" />} 
                  onClick={() => onOpenManualRSVP('declined')}
                >
                  Marquer « Absent »
                </MenuItem>
                <MenuItem 
                  icon={<RepeatIcon color="gray.600" />} 
                  onClick={() => onOpenManualRSVP('pending')}
                >
                  Réinitialiser
                </MenuItem>
                {invitee.response_status === 'accepted' && invitee.contact.email && (
                  <>
                    <MenuDivider />
                    <MenuItem 
                      icon={<EmailIcon color="blue.500" />} 
                      onClick={onResendConfirmation}
                      isDisabled={resendConfirmationLoading}
                    >
                      Confirmation finale
                    </MenuItem>
                  </>
                )}
                <MenuDivider />
                <MenuItem 
                  icon={<DeleteIcon color="red.500" />} 
                  onClick={onRemove}
                >
                  Supprimer
                </MenuItem>
              </MenuList>
            </Menu>
          </HStack>
        </VStack>
      </CardBody>
    </Card>
  );
};

// ============================================
// INVITEES TABLE COMPONENT
// ============================================
interface InviteesTableProps {
  inviteesData: any;
  inviteesLoading: boolean;
  inviteesPage: number;
  setInviteesPage: (page: number) => void;
  inviteesPageSize: number;
  handleRemoveInvitee: (inviteeId: number, contactName: string) => void;
  handleSendInvitation: (
    inviteeId: number,
    contactName: string,
    invitationStatus: string,
  ) => void;
  sendInvitationMutation: any;
  isSelectMode: boolean;
  selectedInvitees: number[];
  toggleInviteeSelection: (inviteeId: number) => void;
  selectAllInvitees: () => void;
  handleOpenManualRSVP: (invitee: InviteeWithDetails, status: string) => void;
  resendFinalConfirmationMutation: any;
  handleOpenResendConfirmation: (inviteeId: number, contactName: string, email: string) => void;
}

const InviteesTable: React.FC<InviteesTableProps> = ({
  inviteesData,
  inviteesLoading,
  inviteesPage,
  setInviteesPage,
  inviteesPageSize,
  handleRemoveInvitee,
  handleSendInvitation,
  sendInvitationMutation,
  isSelectMode,
  selectedInvitees,
  toggleInviteeSelection,
  selectAllInvitees,
  handleOpenManualRSVP,
  resendFinalConfirmationMutation,
  handleOpenResendConfirmation,
}) => {
  // Detect screen size
  const isMobile = useBreakpointValue({ base: true, md: false });
  const isTablet = useBreakpointValue({ base: false, md: true, lg: false });

  if (inviteesLoading) {
    return (
      <Center py={10}>
        <Spinner />
      </Center>
    );
  }

  if (!inviteesData?.items || inviteesData.items.length === 0) {
    return (
      <Box p={8} textAlign="center">
        <Text color="gray.500">Aucun invité ne correspond aux critères</Text>
      </Box>
    );
  }

  const itemsLength = inviteesData?.items?.length ?? 0;

  // MOBILE VIEW - Cards
  if (isMobile) {
    return (
      <VStack spacing={3} align="stretch" p={4}>
        {/* Select all checkbox for mobile */}
        {isSelectMode && (
          <Card bg="blue.50">
            <CardBody py={2}>
              <Checkbox
                isChecked={selectedInvitees.length === itemsLength && itemsLength > 0}
                isIndeterminate={selectedInvitees.length > 0 && selectedInvitees.length < itemsLength}
                onChange={selectAllInvitees}
              >
                <Text fontSize="sm" fontWeight="medium">
                  Tout sélectionner ({selectedInvitees.length}/{itemsLength})
                </Text>
              </Checkbox>
            </CardBody>
          </Card>
        )}

        {inviteesData.items.map((invitee: InviteeWithDetails) => (
          <InviteeCard
            key={invitee.id}
            invitee={invitee}
            onRemove={() => handleRemoveInvitee(invitee.id, invitee.contact.full_name)}
            onSendInvitation={() => handleSendInvitation(
              invitee.id,
              invitee.contact.full_name,
              invitee.invitation_status
            )}
            onOpenManualRSVP={(status) => handleOpenManualRSVP(invitee, status)}
            onResendConfirmation={() => handleOpenResendConfirmation(
              invitee.id,
              invitee.contact.full_name,
              invitee.contact.email || ''
            )}
            isSelected={isSelectMode && selectedInvitees.includes(invitee.id)}
            onToggleSelect={isSelectMode ? () => toggleInviteeSelection(invitee.id) : undefined}
            sendInvitationLoading={sendInvitationMutation.isPending}
            resendConfirmationLoading={resendFinalConfirmationMutation.isPending}
          />
        ))}

        {/* Pagination for mobile */}
        {inviteesData.total_pages > 1 && (
          <Flex justify="center" pt={4}>
            <HStack spacing={2}>
              <IconButton
                icon={<ChevronLeftIcon />}
                aria-label="Page précédente"
                size="sm"
                isDisabled={inviteesPage === 1}
                onClick={() => setInviteesPage(inviteesPage - 1)}
              />
              <Text fontSize="sm" px={2}>
                Page {inviteesPage} / {inviteesData.total_pages}
              </Text>
              <IconButton
                icon={<ChevronRightIcon />}
                aria-label="Page suivante"
                size="sm"
                isDisabled={inviteesPage === inviteesData.total_pages}
                onClick={() => setInviteesPage(inviteesPage + 1)}
              />
            </HStack>
          </Flex>
        )}
      </VStack>
    );
  }

  // TABLET VIEW - Simplified Table
  if (isTablet) {
    return (
      <>
        <Table variant="simple" size="sm">
          <Thead>
            <Tr>
              {isSelectMode && (
                <Th width="40px">
                  <Checkbox
                    isChecked={selectedInvitees.length === itemsLength && itemsLength > 0}
                    isIndeterminate={selectedInvitees.length > 0 && selectedInvitees.length < itemsLength}
                    onChange={selectAllInvitees}
                  />
                </Th>
              )}
              <Th>Contact</Th>
              <Th>VIP</Th>
              <Th>Statut</Th>
              <Th>Réponse</Th>
              <Th>Actions</Th>
            </Tr>
          </Thead>
          <Tbody>
            {inviteesData.items.map((invitee: InviteeWithDetails) => {
              const responseBadge = getResponseStatusBadge(invitee.response_status || 'pending');
              return (
                <Tr
                  key={invitee.id}
                  bg={isSelectMode && selectedInvitees.includes(invitee.id) ? 'blue.50' : undefined}
                >
                  {isSelectMode && (
                    <Td>
                      <Checkbox
                        isChecked={selectedInvitees.includes(invitee.id)}
                        onChange={() => toggleInviteeSelection(invitee.id)}
                      />
                    </Td>
                  )}
                  <Td>
                    <VStack align="start" spacing={0}>
                      <Text fontWeight="medium" fontSize="sm">{invitee.contact.full_name}</Text>
                      <Text fontSize="xs" color="gray.600">
                        {invitee.contact.organization || '-'}
                      </Text>
                      <Text fontSize="xs" color="gray.500">
                        {invitee.contact.email || 'Pas d\'email'}
                      </Text>
                    </VStack>
                  </Td>
                  <Td>
                    <Badge colorScheme={getVIPStatusColor(invitee.effective_vip_status)} fontSize="xs">
                      {getVIPStatusLabel(invitee.effective_vip_status)}
                    </Badge>
                  </Td>
                  <Td>
                    <VStack align="start" spacing={1}>
                      <Badge
                        colorScheme={
                          invitee.invitation_status === 'sent' ? 'green' :
                          invitee.invitation_status === 'pending' ? 'yellow' :
                          'gray'
                        }
                        fontSize="xs"
                      >
                        {invitee.invitation_status === 'sent' ? 'Envoyée' :
                         invitee.invitation_status === 'pending' ? 'En attente' :
                         invitee.invitation_status}
                      </Badge>
                      {invitee.needs_reinvitation && (
                        <Badge colorScheme="orange" fontSize="xs">
                          Réinvitation
                        </Badge>
                      )}
                    </VStack>
                  </Td>
                  <Td>
                    <Badge colorScheme={responseBadge.colorScheme} fontSize="xs">
                      {responseBadge.icon} {responseBadge.label}
                    </Badge>
                  </Td>
                  <Td>
                    {!isSelectMode && (
                      <Menu>
                        <MenuButton
                          as={IconButton}
                          icon={<ChevronDownIcon />}
                          size="xs"
                          variant="outline"
                          aria-label="Actions invité"
                        />
                        <MenuList fontSize="sm">
                          {invitee.contact.email && (
                            <MenuItem
                              icon={<EmailIcon color="blue.500" />}
                              onClick={() =>
                                handleSendInvitation(
                                  invitee.id,
                                  invitee.contact.full_name,
                                  invitee.invitation_status,
                                )
                              }
                              isDisabled={sendInvitationMutation.isPending}
                            >
                              {invitee.invitation_status === 'pending'
                                ? "Envoyer"
                                : "Renvoyer"}
                            </MenuItem>
                          )}
                          <MenuDivider />
                          <MenuItem
                            icon={<CheckIcon color="green.500" />}
                            onClick={() => handleOpenManualRSVP(invitee, 'accepted')}
                          >
                            Présent
                          </MenuItem>
                          <MenuItem
                            icon={<WarningIcon color="orange.500" />}
                            onClick={() => handleOpenManualRSVP(invitee, 'declined')}
                          >
                            Absent
                          </MenuItem>
                          {invitee.response_status === 'accepted' && (
                            <>
                              <MenuDivider />
                              <MenuItem
                                icon={<EmailIcon color="blue.500" />}
                                onClick={() => handleOpenResendConfirmation(
                                  invitee.id,
                                  invitee.contact.full_name,
                                  invitee.contact.email || ''
                                )}
                                isDisabled={!invitee.contact.email || resendFinalConfirmationMutation.isPending}
                              >
                                Confirmation finale
                              </MenuItem>
                            </>
                          )}
                          <MenuDivider />
                          <MenuItem
                            icon={<DeleteIcon color="red.500" />}
                            onClick={() =>
                              handleRemoveInvitee(invitee.id, invitee.contact.full_name)
                            }
                          >
                            Supprimer
                          </MenuItem>
                        </MenuList>
                      </Menu>
                    )}
                  </Td>
                </Tr>
              );
            })}
          </Tbody>
        </Table>

        {/* Pagination for tablet */}
        {inviteesData.total_pages > 1 && (
          <Flex justify="center" p={4}>
            <HStack>
              <Button
                size="sm"
                isDisabled={inviteesPage === 1}
                onClick={() => setInviteesPage(inviteesPage - 1)}
              >
                Précédent
              </Button>
              <Text px={4} fontSize="sm">
                Page {inviteesPage} sur {inviteesData.total_pages}
              </Text>
              <Button
                size="sm"
                isDisabled={inviteesPage === inviteesData.total_pages}
                onClick={() => setInviteesPage(inviteesPage + 1)}
              >
                Suivant
              </Button>
            </HStack>
          </Flex>
        )}
      </>
    );
  }

  // DESKTOP VIEW - Full Table
  return (
    <>
      <Table variant="simple">
        <Thead>
          <Tr>
            {isSelectMode && (
              <Th width="40px">
                <Checkbox
                  isChecked={selectedInvitees.length === itemsLength && itemsLength > 0}
                  isIndeterminate={selectedInvitees.length > 0 && selectedInvitees.length < itemsLength}
                  onChange={selectAllInvitees}
                />
              </Th>
            )}
            <Th>Contact</Th>
            <Th>Organisation</Th>
            <Th>VIP</Th>
            <Th>Statut invitation</Th>
            <Th>Réponse RSVP</Th>
            <Th>Dernier envoi</Th>
            <Th>Actions</Th>
          </Tr>
        </Thead>
        <Tbody>
          {inviteesData?.items?.map((invitee: InviteeWithDetails) => {
            const responseBadge = getResponseStatusBadge(invitee.response_status || 'pending');

            return (
              <Tr
                key={invitee.id}
                bg={isSelectMode && selectedInvitees.includes(invitee.id) ? 'blue.50' : undefined}
              >
                {isSelectMode && (
                  <Td>
                    <Checkbox
                      isChecked={selectedInvitees.includes(invitee.id)}
                      onChange={() => toggleInviteeSelection(invitee.id)}
                    />
                  </Td>
                )}
                <Td>
                  <VStack align="start" spacing={0}>
                    <Text fontWeight="medium">{invitee.contact.full_name}</Text>
                    <Text fontSize="sm" color="gray.600">
                      {invitee.contact.email || 'Pas d\'email'}
                    </Text>
                  </VStack>
                </Td>
                <Td>{invitee.contact.organization || '-'}</Td>
                <Td>
                  <Badge colorScheme={getVIPStatusColor(invitee.effective_vip_status)}>
                    {getVIPStatusLabel(invitee.effective_vip_status)}
                  </Badge>
                </Td>
                <Td>
                  <VStack align="start" spacing={1}>
                    <Badge
                      colorScheme={
                        invitee.invitation_status === 'sent' ? 'green' :
                        invitee.invitation_status === 'pending' ? 'yellow' :
                        'gray'
                      }
                    >
                      {invitee.invitation_status === 'sent' ? 'Envoyée' :
                       invitee.invitation_status === 'pending' ? 'En attente' :
                       invitee.invitation_status}
                    </Badge>
                    {invitee.needs_reinvitation && (
                      <Badge colorScheme="orange" fontSize="xs">
                        Réinvitation requise
                      </Badge>
                    )}
                    {invitee.email_has_changed && (
                      <Badge colorScheme="red" fontSize="xs">
                        Email modifié
                      </Badge>
                    )}
                  </VStack>
                </Td>
                <Td>
                  <VStack align="start" spacing={1}>
                    <Badge colorScheme={responseBadge.colorScheme}>
                      {responseBadge.icon} {responseBadge.label}
                    </Badge>
                    {invitee.response_date && (
                      <Text fontSize="xs" color="gray.500">
                        {new Date(invitee.response_date).toLocaleDateString('fr-FR')}
                      </Text>
                    )}
                  </VStack>
                </Td>
                <Td>
                  <Text fontSize="sm">
                    {invitee.last_invitation_sent_at
                      ? new Date(invitee.last_invitation_sent_at).toLocaleDateString('fr-FR')
                      : '-'
                    }
                  </Text>
                  {invitee.invitation_count > 0 && (
                    <Text fontSize="xs" color="gray.500">
                      {invitee.invitation_count} envoi{invitee.invitation_count > 1 ? 's' : ''}
                    </Text>
                  )}
                </Td>

                <Td>
                  <HStack spacing={2}>
                    {!isSelectMode && (
                      <Menu>
                        <MenuButton
                          as={IconButton}
                          icon={<ChevronDownIcon />}
                          size="sm"
                          variant="outline"
                          aria-label="Actions invité"
                        />
                        <MenuList>
                          {invitee.contact.email && (
                            <MenuItem
                              icon={<EmailIcon color="blue.500" />}
                              onClick={() =>
                                handleSendInvitation(
                                  invitee.id,
                                  invitee.contact.full_name,
                                  invitee.invitation_status,
                                )
                              }
                              isDisabled={sendInvitationMutation.isPending}
                            >
                              {invitee.invitation_status === 'pending'
                                ? "Envoyer l'invitation"
                                : "Renvoyer l'invitation"}
                            </MenuItem>
                          )}

                          <MenuDivider />

                          <MenuItem
                            icon={<CheckIcon color="green.500" />}
                            onClick={() => handleOpenManualRSVP(invitee, 'accepted')}
                          >
                            Marquer « Présent »
                          </MenuItem>

                          <MenuItem
                            icon={<WarningIcon color="orange.500" />}
                            onClick={() => handleOpenManualRSVP(invitee, 'declined')}
                          >
                            Marquer « Absent »
                          </MenuItem>

                          <MenuItem
                            icon={<RepeatIcon color="gray.600" />}
                            onClick={() => handleOpenManualRSVP(invitee, 'pending')}
                          >
                            Réinitialiser la réponse
                          </MenuItem>

                          {invitee.response_status === 'accepted' && (
                            <>
                              <MenuDivider />
                              <MenuItem
                                icon={<EmailIcon color="blue.500" />}
                                onClick={() => handleOpenResendConfirmation(
                                  invitee.id,
                                  invitee.contact.full_name,
                                  invitee.contact.email || ''
                                )}
                                isDisabled={!invitee.contact.email || resendFinalConfirmationMutation.isPending}
                              >
                                Renvoyer confirmation finale
                              </MenuItem>
                            </>
                          )}

                          <MenuDivider />

                          <MenuItem
                            icon={<DeleteIcon color="red.500" />}
                            onClick={() =>
                              handleRemoveInvitee(invitee.id, invitee.contact.full_name)
                            }
                          >
                            Supprimer l'invité
                          </MenuItem>
                        </MenuList>
                      </Menu>
                    )}
                  </HStack>
                </Td>
              </Tr>
            );
          })}
        </Tbody>
      </Table>

      {/* Pagination for desktop */}
      {inviteesData && inviteesData.total_pages > 1 && (
        <Flex justify="center" p={4}>
          <HStack>
            <Button
              size="sm"
              isDisabled={inviteesPage === 1}
              onClick={() => setInviteesPage(inviteesPage - 1)}
            >
              Précédent
            </Button>
            <Text px={4} fontSize="sm">
              Page {inviteesPage} sur {inviteesData.total_pages} ({inviteesData.total} invités)
            </Text>
            <Button
              size="sm"
              isDisabled={inviteesPage === inviteesData.total_pages}
              onClick={() => setInviteesPage(inviteesPage + 1)}
            >
              Suivant
            </Button>
          </HStack>
        </Flex>
      )}
    </>
  );
};

// ============================================
// MAIN COMPONENT: EventDetailPage
// ============================================
const EventDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const toast = useToast();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const [emailsTabOpened, setEmailsTabOpened] = useState(false);
  const showTab = (key: (typeof EVENT_TAB_LABELS extends Record<infer K, any> ? K : never)) =>
    eventTabsFor(user?.role).includes(key);
  const [sendEmailPick, setSendEmailPick] = useState<string | null>(null);

  // Modals
  const {
    isOpen: isAddModalOpen,
    onOpen: onAddModalOpen,
    onClose: onAddModalClose
  } = useDisclosure();
  const {
    isOpen: isPressModalOpen,
    onOpen: onPressModalOpen,
    onClose: onPressModalClose
  } = useDisclosure();
  const {
    isOpen: isSendModalOpen,
    onOpen: onSendModalOpen,
    onClose: onSendModalClose
  } = useDisclosure();
  const {
    isOpen: isSendEmailModalOpen,
    onOpen: onSendEmailModalOpen,
    onClose: onSendEmailModalClose
  } = useDisclosure();
  const {
    isOpen: isRemindersModalOpen,
    onOpen: onRemindersModalOpen,
    onClose: onRemindersModalClose
  } = useDisclosure();

  const {
    isOpen: isFinalConfirmModalOpen,
    onOpen: onFinalConfirmModalOpen,
    onClose: onFinalConfirmModalClose
  } = useDisclosure();

  const {
    isOpen: isManualRSVPModalOpen,
    onOpen: onManualRSVPModalOpen,
    onClose: onManualRSVPModalClose
  } = useDisclosure();

  const {
    isOpen: isSendOneDialogOpen,
    onOpen: onSendOneDialogOpen,
    onClose: onSendOneDialogClose,
  } = useDisclosure();

  const {
    isOpen: isResendConfirmDialogOpen,
    onOpen: onResendConfirmDialogOpen,
    onClose: onResendConfirmDialogClose,
  } = useDisclosure();

  const [sendingFinalConfirmations, setSendingFinalConfirmations] = useState(false);

  // State for invitees list filters
  const [inviteesPage, setInviteesPage] = useState(1);
  const [inviteesPageSize] = useState(50);
  const [inviteesSearchQuery, setInviteesSearchQuery] = useState('');
  const [inviteesVIPFilter, setInviteesVIPFilter] = useState<string>('');
  const [inviteesStatusFilter, setInviteesStatusFilter] = useState<string>('');
  const [inviteesResponseFilter, setInviteesResponseFilter] = useState<string>('');
  const [inviteesDeactivatedOnly, setInviteesDeactivatedOnly] = useState(false);

  // State for bulk selection
  const [isSelectMode, setIsSelectMode] = useState(false);
  const [selectedInvitees, setSelectedInvitees] = useState<number[]>([]);

  // State for add invitees modal
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedContacts, setSelectedContacts] = useState<number[]>([]);
  const [availableContactsPage, setAvailableContactsPage] = useState(1);

  // State for sending invitations
  const [sendingInvitations, setSendingInvitations] = useState(false);
  const [sendProgress, setSendProgress] = useState({ sent: 0, total: 0 });

  // state for sending reminders
  const [sendingReminders, setSendingReminders] = useState(false);

  const [manualRSVPInvitee, setManualRSVPInvitee] = useState<InviteeWithDetails | null>(null);
  const [manualRSVPStatus, setManualRSVPStatus] = useState<string>('accepted');
  const [manualRSVPMethod, setManualRSVPMethod] = useState<string>('phone');
  const [manualRSVPNote, setManualRSVPNote] = useState<string>('');

  const [inviteeToSend, setInviteeToSend] = useState<{
    id: number;
    name: string;
    status: string;
  } | null>(null);

  const [inviteeToResendConfirmation, setInviteeToResendConfirmation] = useState<{
    id: number;
    name: string;
    email: string;
  } | null>(null);

  const cancelRef = useRef<HTMLButtonElement | null>(null);

  // Fetch event details WITH stats
  const { data: event, isLoading: eventLoading } = useQuery({
    queryKey: ['event', id],
    queryFn: () => apiService.getEvent(parseInt(id!), true),
    enabled: !!id,
  });

  // Fetch invitees for the table (paginated)
  const { data: inviteesData, isLoading: inviteesLoading, refetch: refetchInvitees } = useQuery({
    queryKey: ['event-invitees', id, inviteesPage, inviteesSearchQuery, inviteesVIPFilter, inviteesStatusFilter, inviteesResponseFilter, inviteesDeactivatedOnly],
    queryFn: () => apiService.getEventInvitees(parseInt(id!), {
      page: inviteesPage,
      page_size: inviteesPageSize,
      q: inviteesSearchQuery || undefined,
      vip_only: inviteesVIPFilter === 'vip_only' ? true : undefined,
      invitation_status: inviteesStatusFilter || undefined,
      response_status: inviteesResponseFilter || undefined,
      contact_status: inviteesDeactivatedOnly ? 'inactive' : undefined,
      sort_by: 'contact_name',
      sort_order: 'asc',
    }),
    enabled: !!id && !isFrontDesk(user?.role),
  });

  // Get stats from event object (covers ALL invitees, not just current page)
  const eventStats = {
    total: event?.total_invitees || 0,
    responded: event?.responded_count || 0,
    accepted: event?.accepted_count || 0,
    declined: event?.declined_count || 0,
    maybe: event?.maybe_count || 0,
    noResponse: event?.no_response_count || 0,
    checkedIn: event?.checked_in_count || 0,
    vips: event?.vip_count || 0,
    needsReinvitation: event?.contacts_needing_reinvitation_count || 0,
    deactivated: event?.deactivated_invitees_count || 0,
    responseRate: event?.response_rate || 0,
    finalConfirmationPending: event?.final_confirmation_pending_count || 0,
  };

  // Entries of the "Envoyer un e-mail" modal for the 3 system emails: each keeps its legacy flow
  // (own endpoint, own rules, own confirmation dialog). Only the entry point moved.
  const inviteeItems: InviteeWithDetails[] = inviteesData?.items ?? [];
  const systemEmailActions: SystemEmailAction[] = [
    {
      kind: 'invitation',
      label: 'Invitation',
      description: 'Nouveaux invités et réinvitations.',
      count: invitationPendingCount(event),
      countLabel: `${invitationPendingCount(event)} destinataire(s) : invités qui n'ont pas encore reçu l'invitation`,
      lastSend: event?.last_sends?.invitation,
      onSend: () => handleSendInvitations(),
    },
    {
      kind: 'reminder',
      label: 'Rappel',
      description: 'Invités sans réponse.',
      count: event?.reminder_pending_count ?? 0,
      countLabel: `${event?.reminder_pending_count ?? 0} destinataire(s) : invités sans réponse`,
      lastSend: event?.last_sends?.reminder,
      onSend: () => handleSendReminders(),
    },
    {
      kind: 'final_confirmation',
      label: 'Confirmation finale',
      description: 'Invités présents (avec QR code) n’ayant pas encore reçu de confirmation.',
      count: eventStats.finalConfirmationPending,
      countLabel: `${eventStats.finalConfirmationPending} destinataire(s) : invités présents qui n'ont pas encore reçu leur QR code`,
      lastSend: event?.last_sends?.final_confirmation,
      onSend: () => handleSendFinalConfirmations(),
    },
  ];

  // Fetch available contacts for adding
  const { data: availableContactsData, isLoading: availableContactsLoading } = useQuery({
    queryKey: ['available-contacts', id, searchQuery, availableContactsPage],
    queryFn: () => apiService.getAvailableContacts(parseInt(id!), {
      page: availableContactsPage,
      page_size: 20,
      q: searchQuery || undefined,
    }),
    enabled: isAddModalOpen && !!id,
  });

  // Smart Final Confirmations Mutation
  const sendFinalConfirmationsSmartMutation = useMutation({
    mutationFn: async () => {
      setSendingFinalConfirmations(true);
      try {
        const result = await apiService.sendFinalConfirmationsSmart(parseInt(id!));
        return result;
      } finally {
        setSendingFinalConfirmations(false);
      }
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['event-invitees', id] });
      queryClient.invalidateQueries({ queryKey: ['event', id] });
      onFinalConfirmModalClose();

      // Handle case where nothing was sent
      if (data.stats.sent === 0) {
        toast({
          title: 'Aucune confirmation à envoyer',
          description: 'Tous les invités présents ont déjà reçu leur confirmation finale',
          status: 'info',
          duration: 5000,
        });
        return;
      }

      // Build detailed message
      const details = [];
      if (data.stats.reasons.never_sent > 0) {
        details.push(`${data.stats.reasons.never_sent} première(s) confirmation(s)`);
      }
      if (data.stats.reasons.email_changed > 0) {
        details.push(`${data.stats.reasons.email_changed} email(s) mis à jour`);
      }

      const detailsText = details.length > 0 ? ` (${details.join(' + ')})` : '';

      if (data.stats.failed === 0) {
        toast({
          title: '✅ Confirmations finales envoyées',
          description: `${data.stats.sent} confirmation(s) envoyée(s)${detailsText}`,
          status: 'success',
          duration: 5000,
        });
      } else {
        toast({
          title: '⚠️ Confirmations envoyées avec erreurs',
          description: `${data.stats.sent} réussies, ${data.stats.failed} échecs${detailsText}`,
          status: 'warning',
          duration: 7000,
        });
      }
    },
    onError: (error: any) => {
      setSendingFinalConfirmations(false);
      toast({
        title: 'Erreur',
        description: error.message || 'Impossible d\'envoyer les confirmations finales',
        status: 'error',
        duration: 5000,
      });
    },
  });

  // Individual Resend Final Confirmation Mutation
  const resendFinalConfirmationMutation = useMutation({
    mutationFn: ({ inviteeId, contactName }: { inviteeId: number; contactName: string }) =>
      apiService.resendFinalConfirmation(inviteeId),
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['event-invitees', id] });
      toast({
        title: "✅ Confirmation renvoyée",
        description: `Confirmation finale envoyée à ${variables.contactName}`,
        status: 'success',
        duration: 3000,
      });
    },
    onError: (error: any, variables) => {
      toast({
        title: 'Erreur',
        description: error.message || `Impossible d'envoyer la confirmation à ${variables.contactName}`,
        status: 'error',
        duration: 5000,
      });
    },
  });

  // Handler for opening resend confirmation dialog
  const handleOpenResendConfirmation = (inviteeId: number, contactName: string, email: string) => {
    setInviteeToResendConfirmation({
      id: inviteeId,
      name: contactName,
      email: email,
    });
    onResendConfirmDialogOpen();
  };

  const confirmResendConfirmation = () => {
    if (!inviteeToResendConfirmation) return;

    resendFinalConfirmationMutation.mutate({
      inviteeId: inviteeToResendConfirmation.id,
      contactName: inviteeToResendConfirmation.name,
    });

    onResendConfirmDialogClose();
    setInviteeToResendConfirmation(null);
  };

  // Handler for bulk send final confirmations
  const handleSendFinalConfirmations = () => {
    if (eventStats.accepted === 0) {
      toast({
        title: 'Aucun invité présent',
        description: 'Aucun invité n\'a accepté l\'invitation',
        status: 'info',
        duration: 3000,
      });
      return;
    }
    onFinalConfirmModalOpen();
  };

  const confirmSendFinalConfirmations = () => {
    sendFinalConfirmationsSmartMutation.mutate();
  };

  // Publish event mutation
  const publishEventMutation = useMutation({
    mutationFn: () => apiService.publishEvent(parseInt(id!)),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['event', id] });
      toast({
        title: 'Événement publié',
        description: 'L\'événement est maintenant actif et visible',
        status: 'success',
        duration: 3000,
      });
    },
    onError: (error: any) => {
      toast({
        title: 'Erreur',
        description: error.message || 'Impossible de publier l\'événement',
        status: 'error',
        duration: 5000,
      });
    },
  });

  // Unpublish event mutation
  const unpublishEventMutation = useMutation({
    mutationFn: () => apiService.unpublishEvent(parseInt(id!)),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['event', id] });
      toast({
        title: 'Événement dépublié',
        description: 'L\'événement est de nouveau en brouillon',
        status: 'success',
        duration: 3000,
      });
    },
    onError: (error: any) => {
      toast({
        title: 'Erreur',
        description: error.message || 'Impossible de dépublier l\'événement',
        status: 'error',
        duration: 5000,
      });
    },
  });

  // Add invitees mutation
  const addInviteesMutation = useMutation({
    mutationFn: (contactIds: number[]) => apiService.inviteContactsToEvent(parseInt(id!), {
      contact_ids: contactIds,
      send_emails_immediately: false,
    }),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['event-invitees', id] });
      queryClient.invalidateQueries({ queryKey: ['event', id] });
      toast({
        title: 'Invités ajoutés',
        description: data.message,
        status: 'success',
        duration: 3000,
      });
      setSelectedContacts([]);
      onAddModalClose();
    },
    onError: (error: any) => {
      toast({
        title: 'Erreur',
        description: error.message || 'Impossible d\'ajouter les invités',
        status: 'error',
        duration: 5000,
      });
    },
  });

  // Add press conference contacts mutation
  const addPressContactsMutation = useMutation({
    mutationFn: () => apiService.addPressConferenceContacts(parseInt(id!), false),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['event-invitees', id] });
      queryClient.invalidateQueries({ queryKey: ['event', id] });
      toast({
        title: 'Contacts ajoutés',
        description: data.message,
        status: 'success',
        duration: 3000,
      });
      onPressModalClose();
    },
    onError: (error: any) => {
      toast({
        title: 'Erreur',
        description: error.message || 'Impossible d\'ajouter les contacts',
        status: 'error',
        duration: 5000,
      });
    },
  });

  // Remove invitee mutation
  const removeInviteeMutation = useMutation({
    mutationFn: (inviteeId: number) => apiService.removeInvitee(inviteeId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['event-invitees', id] });
      queryClient.invalidateQueries({ queryKey: ['event', id] });
      toast({
        title: 'Invité retiré',
        description: 'L\'invité a été retiré de l\'événement',
        status: 'success',
        duration: 3000,
      });
    },
    onError: (error: any) => {
      toast({
        title: 'Erreur',
        description: error.message || 'Impossible de retirer l\'invité',
        status: 'error',
        duration: 5000,
      });
    },
  });

  // Bulk delete invitees mutation
  const bulkDeleteInviteesMutation = useMutation({
    mutationFn: (inviteeIds: number[]) =>
      apiService.bulkDeleteInvitees(parseInt(id!), inviteeIds),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['event-invitees', id] });
      queryClient.invalidateQueries({ queryKey: ['event', id] });
      toast({
        title: 'Invités supprimés',
        description: data.message,
        status: 'success',
        duration: 3000,
      });
      setSelectedInvitees([]);
      setIsSelectMode(false);
    },
    onError: (error: any) => {
      toast({
        title: 'Erreur',
        description: error.response?.data?.detail || error.message || 'Impossible de supprimer les invités',
        status: 'error',
        duration: 5000,
      });
    },
  });

  // Delete all invitees mutation
  const deleteAllInviteesMutation = useMutation({
    mutationFn: () => apiService.deleteAllInvitees(parseInt(id!)),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['event-invitees', id] });
      queryClient.invalidateQueries({ queryKey: ['event', id] });
      toast({
        title: 'Tous les invités supprimés',
        description: data.message,
        status: 'success',
        duration: 3000,
      });
    },
    onError: (error: any) => {
      toast({
        title: 'Erreur',
        description: error.response?.data?.detail || error.message || 'Impossible de supprimer tous les invités',
        status: 'error',
        duration: 5000,
      });
    },
  });

  // Send single invitation mutation
  const sendInvitationMutation = useMutation({
    mutationFn: ({ inviteeId, contactName }: { inviteeId: number; contactName: string }) =>
      apiService.resendInvitation(inviteeId),
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['event-invitees', id] });
      toast({
        title: "Invitation envoyée",
        description: `Invitation envoyée à ${variables.contactName}`,
        status: 'success',
        duration: 3000,
      });
      onSendOneDialogClose();
      setInviteeToSend(null);
    },
    onError: (error: any, variables) => {
      toast({
        title: 'Erreur',
        description:
          error.message ||
          `Impossible d'envoyer l'invitation à ${variables.contactName}`,
        status: 'error',
        duration: 5000,
      });
    },
  });

  // Send bulk invitations mutation
  const sendInvitationsMutation = useMutation({
    // #60: the whole event, picked server-side (not the page of invitees loaded in the Invités tab)
    mutationFn: async () => {
      const total = invitationPendingCount(event);
      setSendingInvitations(true);
      setSendProgress({ sent: 0, total });
      return sendAllPendingInvitations(
        (data) => apiService.sendInvitations(parseInt(id!), data),
        total,
        setSendProgress
      );
    },
    onSuccess: (results) => {
      queryClient.invalidateQueries({ queryKey: ['event-invitees', id] });
      queryClient.invalidateQueries({ queryKey: ['event', id] });

      setSendingInvitations(false);
      onSendModalClose();

      if (results.failed === 0) {
        toast({
          title: 'Invitations envoyées',
          description: `${results.success} invitations envoyées avec succès`,
          status: 'success',
          duration: 5000,
        });
      } else {
        toast({
          title: 'Envoi terminé avec des erreurs',
          description: `${results.success} réussies, ${results.failed} échecs`,
          status: 'warning',
          duration: 7000,
        });
      }
    },
    onError: (error: any) => {
      setSendingInvitations(false);
      toast({
        title: 'Erreur',
        description: error.message || 'Erreur lors de l\'envoi des invitations',
        status: 'error',
        duration: 5000,
      });
    },
  });

  const sendRemindersMutation = useMutation({
    mutationFn: async () => {
      setSendingReminders(true);
      try {
        const result = await apiService.sendReminders(parseInt(id!));
        return result;
      } finally {
        setSendingReminders(false);
      }
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['event-invitees', id] });
      onRemindersModalClose();

      if (data.reminders_failed === 0) {
        toast({
          title: 'Rappels envoyés',
          description: `${data.reminders_sent} rappel(s) envoyé(s) avec succès`,
          status: 'success',
          duration: 5000,
        });
      } else {
        toast({
          title: 'Rappels envoyés avec des erreurs',
          description: `${data.reminders_sent} réussis, ${data.reminders_failed} échecs`,
          status: 'warning',
          duration: 7000,
        });
      }
    },
    onError: (error: any) => {
      setSendingReminders(false);
      toast({
        title: 'Erreur',
        description: error.message || 'Impossible d\'envoyer les rappels',
        status: 'error',
        duration: 5000,
      });
    },
  });

  // Manual RSVP mutation
  const manualRSVPMutation = useMutation({
    mutationFn: ({ inviteeId, rsvpData }: { inviteeId: number, rsvpData: any }) =>
      apiService.recordManualRSVP(inviteeId, rsvpData),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['event-invitees', id] });
      queryClient.invalidateQueries({ queryKey: ['event', id] });
      toast({
        title: 'Réponse enregistrée',
        description: data.message,
        status: 'success',
        duration: 3000,
      });
      onManualRSVPModalClose();
      setManualRSVPNote('');
    },
    onError: (error: any) => {
      toast({
        title: 'Erreur',
        description: error.message || 'Impossible d\'enregistrer la réponse',
        status: 'error',
        duration: 5000,
      });
    },
  });

  const handleDownloadExcel = async () => {
    try {
      const blob = await apiService.exportEventInvitees(parseInt(id!));

      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `participants_${event?.title?.replace(/[^a-z0-9]/gi, '_')}_${new Date().toISOString().split('T')[0]}.xlsx`;
      document.body.appendChild(a);
      a.click();

      setTimeout(() => {
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      }, 100);

      toast({
        title: 'Export réussi',
        description: 'Le fichier Excel a été téléchargé',
        status: 'success',
        duration: 3000,
      });
    } catch (error: any) {
      toast({
        title: 'Erreur',
        description: error.message || 'Impossible d\'exporter la liste',
        status: 'error',
        duration: 5000,
      });
    }
  };

  const handleSendReminders = () => {
    onRemindersModalOpen();
  };

  const confirmSendReminders = () => {
    sendRemindersMutation.mutate();
  };

  const handleOpenManualRSVP = (invitee: InviteeWithDetails, status: string) => {
    setManualRSVPInvitee(invitee);
    setManualRSVPStatus(status);
    setManualRSVPMethod('phone');
    setManualRSVPNote('');
    onManualRSVPModalOpen();
  };

  const handleSubmitManualRSVP = () => {
    if (!manualRSVPInvitee) return;

    manualRSVPMutation.mutate({
      inviteeId: manualRSVPInvitee.id,
      rsvpData: {
        response_status: manualRSVPStatus,
        response_method: manualRSVPMethod,
        response_note: manualRSVPNote || undefined,
      },
    });
  };

  const handlePublishEvent = () => {
    if (window.confirm('Publier cet événement ? Il deviendra actif et visible.')) {
      publishEventMutation.mutate();
    }
  };

  const handleUnpublishEvent = () => {
    if (window.confirm('Dépublier cet événement ? Il redeviendra un brouillon.')) {
      unpublishEventMutation.mutate();
    }
  };

  const handleAddInvitees = () => {
    if (selectedContacts.length === 0) {
      toast({
        title: 'Aucun contact sélectionné',
        description: 'Veuillez sélectionner au moins un contact',
        status: 'warning',
        duration: 3000,
      });
      return;
    }
    addInviteesMutation.mutate(selectedContacts);
  };

  const handleRemoveInvitee = (inviteeId: number, contactName: string) => {
    if (window.confirm(`Retirer ${contactName} de l'événement ?`)) {
      removeInviteeMutation.mutate(inviteeId);
    }
  };

  const handleBulkDelete = () => {
    if (selectedInvitees.length === 0) {
      toast({
        title: 'Aucun invité sélectionné',
        status: 'warning',
        duration: 3000,
      });
      return;
    }

    if (window.confirm(`Supprimer ${selectedInvitees.length} invité(s) ?`)) {
      bulkDeleteInviteesMutation.mutate(selectedInvitees);
    }
  };

  const handleDeleteAllInvitees = () => {
    const totalInvitees = inviteesData?.total ?? 0;

    const confirmMessage = `⚠️ ATTENTION ⚠️\n\nCette action va supprimer TOUS les ${totalInvitees} invités de cet événement.\n\nCette action est IRRÉVERSIBLE.\n\nTapez "SUPPRIMER" pour confirmer :`;

    const userInput = window.prompt(confirmMessage);

    if (userInput === 'SUPPRIMER') {
      deleteAllInviteesMutation.mutate();
    } else if (userInput !== null) {
      toast({
        title: 'Suppression annulée',
        description: 'Texte de confirmation incorrect',
        status: 'info',
        duration: 3000,
      });
    }
  };

  const handleSendInvitation = (
    inviteeId: number,
    contactName: string,
    invitationStatus: string,
  ) => {
    setInviteeToSend({ id: inviteeId, name: contactName, status: invitationStatus });
    onSendOneDialogOpen();
  };

  const confirmSendOneInvitation = () => {
    if (!inviteeToSend) return;
    sendInvitationMutation.mutate({
      inviteeId: inviteeToSend.id,
      contactName: inviteeToSend.name,
    });
  };

  const handleSendInvitations = () => {
    if (invitationPendingCount(event) === 0) {
      toast({
        title: 'Aucune invitation à envoyer',
        description: 'Toutes les invitations ont déjà été envoyées',
        status: 'info',
        duration: 3000,
      });
      return;
    }

    onSendModalOpen();
  };

  const confirmSendInvitations = () => {
    sendInvitationsMutation.mutate();
  };

  const toggleContactSelection = (contactId: number) => {
    setSelectedContacts(prev =>
      prev.includes(contactId)
        ? prev.filter(id => id !== contactId)
        : [...prev, contactId]
    );
  };

  const toggleInviteeSelection = (inviteeId: number) => {
    setSelectedInvitees(prev =>
      prev.includes(inviteeId)
        ? prev.filter(id => id !== inviteeId)
        : [...prev, inviteeId]
    );
  };

  const selectAllInvitees = () => {
    const itemsLength = inviteesData?.items?.length ?? 0;
    if (selectedInvitees.length === itemsLength) {
      setSelectedInvitees([]);
    } else {
      setSelectedInvitees(inviteesData?.items?.map(inv => inv.id) ?? []);
    }
  };

  const formatDate = (dateString: string) => {
    return formatDateAdmin(dateString);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'published': return 'green';
      case 'draft': return 'yellow';
      case 'cancelled': return 'red';
      case 'completed': return 'blue';
      default: return 'gray';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'published': return '✓ Actif';
      case 'draft': return '📝 Brouillon';
      case 'cancelled': return '✗ Annulé';
      case 'completed': return '✓ Terminé';
      default: return status;
    }
  };

  if (eventLoading) {
    return (
      <Center py={10}>
        <Spinner size="xl" color="blue.500" />
      </Center>
    );
  }

  if (!event) {
    return (
      <Box>
        <Text color="red.500">Événement non trouvé</Text>
      </Box>
    );
  }

  return (
    <Box>

      {/* Header */}

      {/* Header */}
      <VStack spacing={4} mb={6} align="stretch">
        {/* Top Row: Title, Badge, Back */}
        <Flex align="start" justify="space-between" gap={3}>
          <VStack align="start" spacing={2} flex="1" minW={0}>
            <Heading size={{ base: 'md', lg: 'lg' }} noOfLines={2}>
              {event.title}
            </Heading>
            <HStack spacing={2} flexWrap="wrap">
              <Badge colorScheme={getStatusColor(event.status)} fontSize="sm">
                {getStatusLabel(event.status)}
              </Badge>
              {event.location && (
                <Text fontSize="xs" color="gray.600" display={{ base: 'none', md: 'block' }}>
                  📍 {event.location}
                </Text>
              )}
            </HStack>
          </VStack>
          
          <IconButton
            icon={<ChevronLeftIcon />}
            aria-label="Retour"
            variant="ghost"
            size={{ base: 'md', md: 'md' }}
            onClick={() => navigate('/events')}
          />
        </Flex>

        {/* Meta Info Row - Full on mobile too */}
        <Text color="gray.600" fontSize={{ base: 'sm', md: 'md' }}>
          📅 {formatDate(event.event_date)}
          {event.location && (
            <Text as="span" display={{ base: 'inline', md: 'none' }}>
              {' • 📍 ' + event.location}
            </Text>
          )}
        </Text>

        {/* Draft: publishing is the one header action. Everything else lives in the tabs. */}
        {event.status === 'draft' && !isFrontDesk(user?.role) && (
          <Button
            leftIcon={<CheckIcon />}
            colorScheme="green"
            size="lg"
            onClick={handlePublishEvent}
            isLoading={publishEventMutation.isPending}
            w="full"
          >
            Publier l'événement
          </Button>
        )}
      </VStack>


      {/* Status explanation alert */}
      {event.status === 'draft' && (
        <Alert status="info" mb={6}>
          <AlertIcon />
          <Box flex="1">
            <AlertTitle fontSize={{ base: 'sm', md: 'md' }}>Événement en brouillon</AlertTitle>
            <AlertDescription fontSize={{ base: 'xs', md: 'sm' }}>
              Ajoutez des invités et publiez-le pour activer l'envoi d'invitations.
            </AlertDescription>
          </Box>
        </Alert>
      )}

      {/* Stats */}
      <SimpleGrid 
        columns={{ base: 1, sm: 2, md: 3, lg: 6 }} 
        spacing={{ base: 3, md: 6 }} 
        mb={6}
      >
        <Card>
          <CardBody>
            <Stat>
              <StatLabel fontSize={{ base: 'xs', md: 'sm' }}>Total invités</StatLabel>
              <StatNumber fontSize={{ base: 'xl', md: '2xl' }}>{eventStats.total}</StatNumber>
            </Stat>
          </CardBody>
        </Card>

        <Card>
          <CardBody>
            <Stat>
              <StatLabel fontSize={{ base: 'xs', md: 'sm' }}>Réponses</StatLabel>
              <StatNumber fontSize={{ base: 'xl', md: '2xl' }}>{eventStats.responded}</StatNumber>
              <StatHelpText fontSize={{ base: 'xs', md: 'sm' }}>
                {eventStats.responseRate.toFixed(0)}%
              </StatHelpText>
            </Stat>
          </CardBody>
        </Card>

        <Card>
          <CardBody>
            <Stat>
              <StatLabel fontSize={{ base: 'xs', md: 'sm' }}>Présents</StatLabel>
              <StatNumber fontSize={{ base: 'xl', md: '2xl' }} color="green.500">
                {eventStats.accepted}
              </StatNumber>
            </Stat>
          </CardBody>
        </Card>

        <Card>
          <CardBody>
            <Stat>
              <StatLabel fontSize={{ base: 'xs', md: 'sm' }}>Absents</StatLabel>
              <StatNumber fontSize={{ base: 'xl', md: '2xl' }} color="orange.500">
                {eventStats.declined}
              </StatNumber>
            </Stat>
          </CardBody>
        </Card>

        <Card>
          <CardBody>
            <Stat>
              <StatLabel fontSize={{ base: 'xs', md: 'sm' }}>Sans réponse</StatLabel>
              <StatNumber fontSize={{ base: 'xl', md: '2xl' }} color="gray.500">
                {eventStats.noResponse}
              </StatNumber>
            </Stat>
          </CardBody>
        </Card>

        <Card>
          <CardBody>
            <Stat>
              <StatLabel fontSize={{ base: 'xs', md: 'sm' }}>Check-in</StatLabel>
              <StatNumber fontSize={{ base: 'xl', md: '2xl' }}>{eventStats.checkedIn}</StatNumber>
              <StatHelpText fontSize={{ base: 'xs', md: 'sm' }}>
                {eventStats.accepted > 0
                  ? `${Math.round((eventStats.checkedIn / eventStats.accepted) * 100)}%`
                  : '0%'
                }
              </StatHelpText>
            </Stat>
          </CardBody>
        </Card>
      </SimpleGrid>

      {/* Optional info alert */}
      {eventStats.total > 0 && eventStats.responded === 0 && (
        <Alert status="info" mb={6}>
          <AlertIcon />
          <AlertDescription fontSize={{ base: 'xs', md: 'sm' }}>
            Aucune réponse reçue pour le moment.
          </AlertDescription>
        </Alert>
      )}

      {/* Reinvitation alert */}
      {eventStats.needsReinvitation > 0 && !isFrontDesk(user?.role) && (
        <Alert status="warning" mb={6}>
          <AlertIcon />
          <Box flex="1">
            <AlertTitle fontSize={{ base: 'sm', md: 'md' }}>Réinvitations nécessaires</AlertTitle>
            <AlertDescription fontSize={{ base: 'xs', md: 'sm' }}>
              <VStack align="start" spacing={1} mt={2}>
                <Text>
                  {eventStats.needsReinvitation} invité{eventStats.needsReinvitation > 1 ? 's' : ''}
                  {' '}nécessite{eventStats.needsReinvitation > 1 ? 'nt' : ''} une réinvitation
                </Text>
                {event?.reinvitation_breakdown && (
                  <VStack align="start" spacing={0} pl={4}>
                    {event.reinvitation_breakdown.email_changed > 0 && (
                      <Text fontSize="xs">• {event.reinvitation_breakdown.email_changed} email(s) modifié(s)</Text>
                    )}
                    {event.reinvitation_breakdown.newly_added > 0 && (
                      <Text fontSize="xs">• {event.reinvitation_breakdown.newly_added} nouvel(aux) invité(s)</Text>
                    )}
                    {event.reinvitation_breakdown.bounced > 0 && (
                      <Text fontSize="xs">• {event.reinvitation_breakdown.bounced} bounce(s)</Text>
                    )}
                  </VStack>
                )}
              </VStack>
            </AlertDescription>
          </Box>
          <Button
            colorScheme="orange"
            size="sm"
            onClick={() => {
              setInviteesStatusFilter('');
              setInviteesSearchQuery('');
              setInviteesVIPFilter('');
              setInviteesResponseFilter('');
            }}
          >
            Voir
          </Button>
        </Alert>
      )}

      {/* Tabs */}
      <Tabs
        defaultIndex={defaultEventTabIndexFor(user?.role)}
        onChange={(index) => { if (eventTabsFor(user?.role)[index] === 'settings') setEmailsTabOpened(true); }}
      >
        <TabList overflowX="auto" overflowY="hidden">
          {eventTabsFor(user?.role).map((key) => (
            <Tab key={key} fontSize={{ base: 'sm', md: 'md' }} whiteSpace="nowrap" data-testid={'tab-' + key}>
              {EVENT_TAB_LABELS[key]}
            </Tab>
          ))}
        </TabList>

        <TabPanels>
          {/* Settings tab: event information (read-only), design of the emails, lifecycle actions */}
          {showTab('settings') && (
          <TabPanel px={{ base: 0, md: 4 }}>
            <VStack spacing={4} align="stretch">
              <SectionCard title="Actions" testId="settings-section-actions">
              <HStack spacing={2} flexWrap="wrap">
                <Button
                  leftIcon={<EditIcon />}
                  colorScheme="blue"
                  onClick={() => navigate(`/events/${id}/edit`)}
                  data-testid="settings-edit-button"
                >
                  Modifier
                </Button>
                {event.status !== 'draft' && (
                  <Button
                    leftIcon={<WarningIcon />}
                    variant="outline"
                    onClick={handleUnpublishEvent}
                    isDisabled={unpublishEventMutation.isPending}
                  >
                    Dépublier (brouillon)
                  </Button>
                )}
                <Button
                  leftIcon={<DeleteIcon />}
                  variant="outline"
                  colorScheme="red"
                  onClick={() => {
                    if (window.confirm('Supprimer cet événement ?')) {
                      // Add delete handler
                    }
                  }}
                >
                  Supprimer l'événement
                </Button>
              </HStack>
              </SectionCard>
              <SectionCard title="Informations générales" testId="settings-section-info">
                  <VStack align="stretch" spacing={4}>
                    {event.short_description && (
                      <Box>
                        <Text fontWeight="bold" fontSize="sm" color="gray.600">
                          Description courte
                        </Text>
                        <Text fontSize={{ base: 'sm', md: 'md' }}>{event.short_description}</Text>
                      </Box>
                    )}
                    {event.description && (
                      <Box>
                        <Text fontWeight="bold" fontSize="sm" color="gray.600">
                          Description complète
                        </Text>
                        <Text fontSize={{ base: 'sm', md: 'md' }} whiteSpace="pre-wrap">
                          {event.description}
                        </Text>
                      </Box>
                    )}
                    <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                      <Box>
                        <Text fontWeight="bold" fontSize="sm" color="gray.600">
                          Type d'événement
                        </Text>
                        <Text fontSize={{ base: 'sm', md: 'md' }}>{event.event_type}</Text>
                      </Box>
                      <Box>
                        <Text fontWeight="bold" fontSize="sm" color="gray.600">
                          Date de fin
                        </Text>
                        <Text fontSize={{ base: 'sm', md: 'md' }}>
                          {event.event_end_date ? formatDate(event.event_end_date) : '-'}
                        </Text>
                      </Box>
                      {event.response_deadline && (
                        <Box>
                          <Text fontWeight="bold" fontSize="sm" color="gray.600">
                            Date limite de réponse
                          </Text>
                          <Text fontSize={{ base: 'sm', md: 'md' }}>
                            {formatDate(event.response_deadline)}
                          </Text>
                        </Box>
                      )}
                      <Box>
                        <Text fontWeight="bold" fontSize="sm" color="gray.600">
                          Réponses tardives
                        </Text>
                        <Text fontSize={{ base: 'sm', md: 'md' }}>
                          {event.allow_late_responses ? 'Autorisées' : 'Non autorisées'}
                        </Text>
                      </Box>
                    </SimpleGrid>
                  </VStack>
              </SectionCard>

              {event.internal_notes && (
                <SectionCard title="Notes internes" testId="settings-section-notes">
                  <Text fontSize={{ base: 'sm', md: 'md' }} whiteSpace="pre-wrap">
                    {event.internal_notes}
                  </Text>
                </SectionCard>
              )}

              {user?.role === 'admin' && event && (
                <SectionCard title="Équipe" testId="settings-section-team">
                  <EventTeamSection eventId={event.id} />
                </SectionCard>
              )}

              <SectionCard title="Design des e-mails" testId="settings-section-emails">
                {/* Mounted on first open (the first load creates the event's default emails),
                    then kept mounted so unsaved edits survive a tab switch. */}
                {emailsTabOpened && event && <EmailsTab eventId={event.id} />}
              </SectionCard>
            </VStack>
          </TabPanel>
          )}

          {/* Invitees Tab */}
          {showTab('guests') && (
          <TabPanel px={{ base: 0, md: 4 }}>
            <Card variant="outline" data-testid="guests-section">
              <CardHeader>
                <Flex align="center" mb={4} direction={{ base: 'column', md: 'row' }} gap={3}>
                  <Heading size="sm" flex={{ base: 'none', md: '1' }}>
                    Liste des invités
                  </Heading>
                  
                  <HStack spacing={2} flexWrap="wrap" w={{ base: 'full', md: 'auto' }}>
                    <Button
                      leftIcon={<DownloadIcon />}
                      variant="outline"
                      size="sm"
                      onClick={handleDownloadExcel}
                      isDisabled={eventStats.total === 0}
                      flex={{ base: '1', md: 'initial' }}
                      data-testid="export-excel-button"
                    >
                      Export Excel
                    </Button>
                    {(inviteesData?.items?.length ?? 0) > 0 && (
                      <>
                        <Button
                          size="sm"
                          variant={isSelectMode ? "solid" : "outline"}
                          colorScheme={isSelectMode ? "blue" : "gray"}
                          onClick={() => {
                            setIsSelectMode(!isSelectMode);
                            setSelectedInvitees([]);
                          }}
                          flex={{ base: '1', md: 'initial' }}
                        >
                          {isSelectMode ? 'Annuler' : 'Sélection'}
                        </Button>

                        {isSelectMode && (
                          <Button
                            size="sm"
                            colorScheme="red"
                            leftIcon={<DeleteIcon />}
                            onClick={handleBulkDelete}
                            isDisabled={selectedInvitees.length === 0}
                            isLoading={bulkDeleteInviteesMutation.isPending}
                            flex={{ base: '1', md: 'initial' }}
                          >
                            ({selectedInvitees.length})
                          </Button>
                        )}

                        <Button
                          size="sm"
                          colorScheme="red"
                          variant="outline"
                          onClick={handleDeleteAllInvitees}
                          isLoading={deleteAllInviteesMutation.isPending}
                          display={{ base: 'none', md: 'flex' }}
                        >
                          Tout supprimer
                        </Button>
                      </>
                    )}

                    <Tooltip
                      label="Importer liste présélectionnée"
                      hasArrow
                      placement="bottom"
                    >
                      <Button
                        leftIcon={<AddIcon />}
                        colorScheme="green"
                        size="sm"
                        onClick={onPressModalOpen}
                        flex={{ base: '1', md: 'initial' }}
                      >
                        Importer
                      </Button>
                    </Tooltip>

                    <Tooltip
                      label="Sélectionner manuellement"
                      hasArrow
                      placement="bottom"
                    >
                      <Button
                        leftIcon={<AddIcon />}
                        colorScheme="blue"
                        size="sm"
                        onClick={onAddModalOpen}
                        flex={{ base: '1', md: 'initial' }}
                      >
                        Ajouter
                      </Button>
                    </Tooltip>
                  </HStack>
                </Flex>

                {/* Filters - Mobile: Vertical Stack, Desktop: Horizontal */}
                <VStack 
                  spacing={3} 
                  mb={4} 
                  align="stretch" 
                  display={{ base: 'flex', md: 'none' }}
                >
                  <InputGroup>
                    <InputLeftElement pointerEvents="none">
                      <SearchIcon color="gray.400" />
                    </InputLeftElement>
                    <Input
                      placeholder="Rechercher..."
                      value={inviteesSearchQuery}
                      onChange={(e) => {
                        setInviteesSearchQuery(e.target.value);
                        setInviteesPage(1);
                      }}
                      autoComplete="off"
                      size="sm"
                    />
                  </InputGroup>
                  <Select
                    placeholder="Statut VIP"
                    value={inviteesVIPFilter}
                    onChange={(e) => {
                      setInviteesVIPFilter(e.target.value);
                      setInviteesPage(1);
                    }}
                    size="sm"
                  >
                    <option value="vip_only">VIPs uniquement</option>
                  </Select>
                  <Select
                    placeholder="Statut invitation"
                    value={inviteesStatusFilter}
                    onChange={(e) => {
                      setInviteesStatusFilter(e.target.value);
                      setInviteesPage(1);
                    }}
                    size="sm"
                  >
                    <option value="pending">En attente</option>
                    <option value="sent">Envoyée</option>
                  </Select>
                  <Select
                    placeholder="Réponse RSVP"
                    value={inviteesResponseFilter}
                    onChange={(e) => {
                      setInviteesResponseFilter(e.target.value);
                      setInviteesPage(1);
                    }}
                    size="sm"
                  >
                    <option value="pending">Pas encore répondu</option>
                    <option value="accepted">✓ Présent</option>
                    <option value="declined">✗ Absent</option>
                    <option value="maybe">? Peut-être</option>
                  </Select>
                  {(inviteesSearchQuery || inviteesVIPFilter || inviteesStatusFilter || inviteesResponseFilter || inviteesDeactivatedOnly) && (
                    <Button
                      size="sm"
                      onClick={() => {
                        setInviteesSearchQuery('');
                        setInviteesVIPFilter('');
                        setInviteesStatusFilter('');
                        setInviteesResponseFilter('');
                        setInviteesDeactivatedOnly(false);
                        setInviteesPage(1);
                      }}
                    >
                      Réinitialiser
                    </Button>
                  )}
                </VStack>

                {/* Desktop filters */}
                <HStack 
                  spacing={4} 
                  mb={4} 
                  display={{ base: 'none', md: 'flex' }}
                  flexWrap="wrap"
                >
                  <InputGroup maxW="300px">
                    <InputLeftElement pointerEvents="none">
                      <SearchIcon color="gray.400" />
                    </InputLeftElement>
                    <Input
                      placeholder="Rechercher un invité..."
                      value={inviteesSearchQuery}
                      onChange={(e) => {
                        setInviteesSearchQuery(e.target.value);
                        setInviteesPage(1);
                      }}
                      autoComplete="off"
                    />
                  </InputGroup>

                  <Select
                    placeholder="Tous les statuts VIP"
                    value={inviteesVIPFilter}
                    onChange={(e) => {
                      setInviteesVIPFilter(e.target.value);
                      setInviteesPage(1);
                    }}
                    maxW="200px"
                  >
                    <option value="vip_only">VIPs uniquement</option>
                  </Select>

                  <Select
                    placeholder="Statut invitation"
                    value={inviteesStatusFilter}
                    onChange={(e) => {
                      setInviteesStatusFilter(e.target.value);
                      setInviteesPage(1);
                    }}
                    maxW="200px"
                  >
                    <option value="pending">En attente</option>
                    <option value="sent">Envoyée</option>
                  </Select>

                  <Select
                    placeholder="Réponse RSVP"
                    value={inviteesResponseFilter}
                    onChange={(e) => {
                      setInviteesResponseFilter(e.target.value);
                      setInviteesPage(1);
                    }}
                    maxW="200px"
                  >
                    <option value="pending">Pas encore répondu</option>
                    <option value="accepted">✓ Présent</option>
                    <option value="declined">✗ Absent</option>
                    <option value="maybe">? Peut-être</option>
                  </Select>

                  {(inviteesSearchQuery || inviteesVIPFilter || inviteesStatusFilter || inviteesResponseFilter || inviteesDeactivatedOnly) && (
                    <Button
                      size="sm"
                      onClick={() => {
                        setInviteesSearchQuery('');
                        setInviteesVIPFilter('');
                        setInviteesStatusFilter('');
                        setInviteesResponseFilter('');
                        setInviteesDeactivatedOnly(false);
                        setInviteesPage(1);
                      }}
                    >
                      Réinitialiser
                    </Button>
                  )}
                </HStack>
              </CardHeader>
              <CardBody p={0}>
                <DeactivatedInviteesNotice
                  count={eventStats.deactivated}
                  active={inviteesDeactivatedOnly}
                  onToggle={() => {
                    setInviteesDeactivatedOnly(!inviteesDeactivatedOnly);
                    setInviteesPage(1);
                  }}
                />
                <InviteesTable
                  inviteesData={inviteesData}
                  inviteesLoading={inviteesLoading}
                  inviteesPage={inviteesPage}
                  setInviteesPage={setInviteesPage}
                  inviteesPageSize={inviteesPageSize}
                  handleRemoveInvitee={handleRemoveInvitee}
                  handleSendInvitation={handleSendInvitation}
                  sendInvitationMutation={sendInvitationMutation}
                  isSelectMode={isSelectMode}
                  selectedInvitees={selectedInvitees}
                  toggleInviteeSelection={toggleInviteeSelection}
                  selectAllInvitees={selectAllInvitees}
                  handleOpenManualRSVP={handleOpenManualRSVP}
                  resendFinalConfirmationMutation={resendFinalConfirmationMutation}
                  handleOpenResendConfirmation={handleOpenResendConfirmation}
                />
              </CardBody>
            </Card>
          </TabPanel>
          )}

          {/* Send emails tab */}
          {showTab('send-emails') && (
          <TabPanel px={{ base: 0, md: 4 }}>
            <SendEmailsTab
              eventId={event.id}
              systemEmails={systemEmailActions}
              canSend={event.status !== 'draft' && (inviteesData?.items?.length ?? 0) > 0}
              isDraft={event.status === 'draft'}
              onSend={(pick) => { setSendEmailPick(pick); onSendEmailModalOpen(); }}
            />
          </TabPanel>
          )}

          {/* Check-in tab */}
          {showTab('check-in') && (
          <TabPanel px={{ base: 0, md: 4 }}>
            <CheckinTab
              present={eventStats.checkedIn}
              expected={eventStats.accepted}
              onManual={() => navigate(`/events/${id}/checkin`)}
              onScanner={() => navigate(`/events/${id}/qr-scanner`)}
              vipBoardUrl={event.vip_board_uuid ? `${window.location.origin}/vip-board/${event.vip_board_uuid}` : null}
              onCopyVipBoard={() => {
                navigator.clipboard.writeText(`${window.location.origin}/vip-board/${event.vip_board_uuid}`);
                toast({ title: '✓ Lien copié', description: 'Lien du tableau VIP copié', status: 'success', duration: 3000 });
              }}
            />
          </TabPanel>
          )}
        </TabPanels>
      </Tabs>

      {/* Add Invitees Modal */}
      <Modal isOpen={isAddModalOpen} onClose={onAddModalClose} size={{ base: 'full', md: 'xl' }}>
        <ModalOverlay />
        <ModalContent mx={{ base: 0, md: 4 }}>
          <ModalHeader fontSize={{ base: 'md', md: 'lg' }}>Ajouter des invités</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4}>
              <InputGroup>
                <InputLeftElement pointerEvents="none">
                  <SearchIcon color="gray.400" />
                </InputLeftElement>
                <Input
                  placeholder="Rechercher des contacts..."
                  value={searchQuery}
                  onChange={(e) => {
                    setSearchQuery(e.target.value);
                    setAvailableContactsPage(1);
                  }}
                  autoComplete="off"
                  size={{ base: 'sm', md: 'md' }}
                />
              </InputGroup>

              {availableContactsLoading ? (
                <Center py={10}>
                  <Spinner />
                </Center>
              ) : (availableContactsData?.items?.length ?? 0) === 0 ? (
                <Text color="gray.500" fontSize={{ base: 'sm', md: 'md' }}>
                  Aucun contact disponible
                </Text>
              ) : (
                <>
                  <Box w="full" maxH="400px" overflowY="auto">
                    <VStack spacing={2} align="stretch">
                      {availableContactsData?.items?.map((contact: Contact) => (
                        <Card
                          key={contact.id}
                          bg={selectedContacts.includes(contact.id) ? 'blue.50' : 'white'}
                          cursor="pointer"
                          onClick={() => toggleContactSelection(contact.id)}
                          _hover={{ bg: 'gray.50' }}
                        >
                          <CardBody py={3}>
                            <Flex align="center">
                              <VStack align="start" spacing={0} flex="1">
                                <Text fontWeight="medium" fontSize={{ base: 'sm', md: 'md' }}>
                                  {contact.full_name}
                                </Text>
                                <Text fontSize={{ base: 'xs', md: 'sm' }} color="gray.600">
                                  {contact.email || 'Pas d\'email'}
                                </Text>
                                {contact.organization && (
                                  <Text fontSize={{ base: 'xs', md: 'sm' }} color="gray.500">
                                    {contact.organization}
                                  </Text>
                                )}
                              </VStack>
                              {contact.global_vip_status !== 'none' && (
                                <Badge 
                                  colorScheme={getVIPStatusColor(contact.global_vip_status)}
                                  fontSize="xs"
                                >
                                  {getVIPStatusLabel(contact.global_vip_status)}
                                </Badge>
                              )}
                            </Flex>
                          </CardBody>
                        </Card>
                      ))}
                    </VStack>
                  </Box>

                  {availableContactsData && availableContactsData.total_pages > 1 && (
                    <Flex justify="center" w="full">
                      <HStack>
                        <IconButton
                          icon={<ChevronLeftIcon />}
                          aria-label="Précédent"
                          size="sm"
                          isDisabled={availableContactsPage === 1}
                          onClick={() => setAvailableContactsPage(availableContactsPage - 1)}
                        />
                        <Text fontSize="sm">
                          {availableContactsPage} / {availableContactsData.total_pages}
                        </Text>
                        <IconButton
                          icon={<ChevronRightIcon />}
                          aria-label="Suivant"
                          size="sm"
                          isDisabled={availableContactsPage === availableContactsData.total_pages}
                          onClick={() => setAvailableContactsPage(availableContactsPage + 1)}
                        />
                      </HStack>
                    </Flex>
                  )}
                </>
              )}

              {selectedContacts.length > 0 && (
                <Alert status="info">
                  <AlertIcon />
                  <Text fontSize={{ base: 'xs', md: 'sm' }}>
                    {selectedContacts.length} contact(s) sélectionné(s)
                  </Text>
                </Alert>
              )}
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onAddModalClose} size={{ base: 'sm', md: 'md' }}>
              Annuler
            </Button>
            <Button
              colorScheme="blue"
              onClick={handleAddInvitees}
              isLoading={addInviteesMutation.isPending}
              isDisabled={selectedContacts.length === 0}
              size={{ base: 'sm', md: 'md' }}
            >
              Ajouter {selectedContacts.length > 0 && `(${selectedContacts.length})`}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Add Press Conference Contacts Modal */}
      <Modal isOpen={isPressModalOpen} onClose={onPressModalClose} size={{ base: 'full', md: 'md' }}>
        <ModalOverlay />
        <ModalContent mx={{ base: 0, md: 4 }}>
          <ModalHeader fontSize={{ base: 'md', md: 'lg' }}>
            Ajouter tous les contacts présélectionnés
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4}>
              <Alert status="info">
                <AlertIcon />
                <Text fontSize={{ base: 'xs', md: 'sm' }}>
                  Cette action ajoutera tous les contacts marqués "Conférence de presse" dans le CRM.
                </Text>
              </Alert>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onPressModalClose} size={{ base: 'sm', md: 'md' }}>
              Annuler
            </Button>
            <Button
              colorScheme="green"
              onClick={() => addPressContactsMutation.mutate()}
              isLoading={addPressContactsMutation.isPending}
              size={{ base: 'sm', md: 'md' }}
            >
              Ajouter présélection
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Send Invitations Modal */}
      <Modal isOpen={isSendModalOpen} onClose={onSendModalClose} size={{ base: 'full', md: 'lg' }}>
        <ModalOverlay />
        <ModalContent mx={{ base: 0, md: 4 }}>
          <ModalHeader fontSize={{ base: 'md', md: 'lg' }}>Envoyer les invitations</ModalHeader>
          <ModalCloseButton isDisabled={sendingInvitations} />
          <ModalBody>
            <VStack spacing={4} align="stretch">
              {!sendingInvitations ? (
                <>
                  <Alert status="info">
                    <AlertIcon />
                    <Box>
                      <AlertDescription fontSize={{ base: 'xs', md: 'sm' }}>
                        <VStack align="start" spacing={2}>
                          <Text fontWeight="bold">
                            Cette action va envoyer des invitations par email à :
                          </Text>
                          <Text>
                            • Nouveaux invités et réinvitations nécessaires
                          </Text>
                        </VStack>
                      </AlertDescription>
                    </Box>
                  </Alert>

                  <Box p={4} bg="blue.50" borderRadius="md">
                    <VStack align="start" spacing={2}>
                      <Text fontWeight="bold" fontSize={{ base: 'xs', md: 'sm' }}>
                        Total à envoyer: {invitationPendingCount(event)} invitations
                      </Text>
                      <Text fontSize={{ base: 'xs', md: 'sm' }} color="gray.600">
                        Les emails seront envoyés par lots pour assurer la fiabilité
                      </Text>
                    </VStack>
                  </Box>

                  {event.response_deadline && (
                    <Alert status="info" size="sm">
                      <AlertIcon />
                      <Text fontSize={{ base: 'xs', md: 'sm' }}>
                        Date limite de réponse: {formatDate(event.response_deadline)}
                      </Text>
                    </Alert>
                  )}
                </>
              ) : (
                <>
                  <Alert status="info">
                    <AlertIcon />
                    <Text fontSize={{ base: 'xs', md: 'sm' }}>
                      Envoi en cours... Ne fermez pas cette fenêtre.
                    </Text>
                  </Alert>

                  <Box>
                    <Flex justify="space-between" mb={2}>
                      <Text fontSize={{ base: 'xs', md: 'sm' }} fontWeight="medium">
                        Progression
                      </Text>
                      <Text fontSize={{ base: 'xs', md: 'sm' }} color="gray.600">
                        {sendProgress.sent} / {sendProgress.total}
                      </Text>
                    </Flex>
                    <Progress
                      value={(sendProgress.sent / sendProgress.total) * 100}
                      size="sm"
                      colorScheme="green"
                      hasStripe
                      isAnimated
                    />
                  </Box>
                </>
              )}
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button
              variant="ghost"
              mr={3}
              onClick={onSendModalClose}
              isDisabled={sendingInvitations}
              size={{ base: 'sm', md: 'md' }}
            >
              Annuler
            </Button>
            <Button
              colorScheme="green"
              onClick={confirmSendInvitations}
              isLoading={sendingInvitations}
              loadingText={`${sendProgress.sent}/${sendProgress.total}...`}
              size={{ base: 'sm', md: 'md' }}
            >
              Confirmer l'envoi
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Send Reminders Modal */}
      <Modal isOpen={isRemindersModalOpen} onClose={onRemindersModalClose} size={{ base: 'full', md: 'lg' }}>
        <ModalOverlay />
        <ModalContent mx={{ base: 0, md: 4 }}>
          <ModalHeader fontSize={{ base: 'md', md: 'lg' }}>Envoyer des rappels</ModalHeader>
          <ModalCloseButton isDisabled={sendingReminders} />
          <ModalBody>
            <VStack spacing={4} align="stretch">
              {!sendingReminders ? (
                <>
                  <Alert status="info">
                    <AlertIcon />
                    <Box>
                      <AlertDescription fontSize={{ base: 'xs', md: 'sm' }}>
                        <VStack align="start" spacing={2}>
                          <Text fontWeight="bold">
                            Envoi de rappels aux invités sans réponse :
                          </Text>
                          <Text>
                            • Invités sans réponse : {eventStats.noResponse}
                          </Text>
                        </VStack>
                      </AlertDescription>
                    </Box>
                  </Alert>

                  <Box p={4} bg="orange.50" borderRadius="md">
                    <VStack align="start" spacing={2}>
                      <Text fontWeight="bold" fontSize={{ base: 'xs', md: 'sm' }}>
                        Total à envoyer : {eventStats.noResponse} rappel(s)
                      </Text>
                      <Text fontSize={{ base: 'xs', md: 'sm' }} color="gray.600">
                        Les emails seront envoyés par lots
                      </Text>
                    </VStack>
                  </Box>

                  {event.response_deadline && (
                    <Alert status="info" size="sm">
                      <AlertIcon />
                      <Text fontSize={{ base: 'xs', md: 'sm' }}>
                        Date limite : {formatDate(event.response_deadline)}
                      </Text>
                    </Alert>
                  )}
                </>
              ) : (
                <>
                  <Alert status="info">
                    <AlertIcon />
                    <Text fontSize={{ base: 'xs', md: 'sm' }}>
                      Envoi des rappels en cours...
                    </Text>
                  </Alert>

                  <Box>
                    <Progress
                      size="sm"
                      colorScheme="orange"
                      isIndeterminate
                    />
                  </Box>
                </>
              )}
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button
              variant="ghost"
              mr={3}
              onClick={onRemindersModalClose}
              isDisabled={sendingReminders}
              size={{ base: 'sm', md: 'md' }}
            >
              Annuler
            </Button>
            <Button
              colorScheme="orange"
              onClick={confirmSendReminders}
              isLoading={sendingReminders}
              loadingText="Envoi..."
              size={{ base: 'sm', md: 'md' }}
            >
              Confirmer
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Manual RSVP Modal */}
      <Modal isOpen={isManualRSVPModalOpen} onClose={onManualRSVPModalClose} size={{ base: 'full', md: 'md' }}>
        <ModalOverlay />
        <ModalContent mx={{ base: 0, md: 4 }}>
          <ModalHeader fontSize={{ base: 'md', md: 'lg' }}>Enregistrer une réponse manuelle</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4} align="stretch">
              {manualRSVPInvitee && (
                <Alert status="info" size="sm">
                  <AlertIcon />
                  <Box>
                    <Text fontWeight="bold" fontSize={{ base: 'sm', md: 'md' }}>
                      {manualRSVPInvitee.contact.full_name}
                    </Text>
                    <Text fontSize={{ base: 'xs', md: 'sm' }}>
                      {manualRSVPInvitee.contact.email || 'Pas d\'email'}
                    </Text>
                    {manualRSVPInvitee.contact.organization && (
                      <Text fontSize={{ base: 'xs', md: 'sm' }} color="gray.600">
                        {manualRSVPInvitee.contact.organization}
                      </Text>
                    )}
                  </Box>
                </Alert>
              )}

              <FormControl>
                <FormLabel fontSize={{ base: 'sm', md: 'md' }}>Statut de réponse</FormLabel>
                <RadioGroup value={manualRSVPStatus} onChange={setManualRSVPStatus}>
                  <Stack>
                    <Radio value="accepted" colorScheme="green">
                      <Text fontSize={{ base: 'sm', md: 'md' }}>✓ Présent</Text>
                    </Radio>
                    <Radio value="declined" colorScheme="red">
                      <Text fontSize={{ base: 'sm', md: 'md' }}>✗ Absent</Text>
                    </Radio>
                    <Radio value="maybe" colorScheme="orange">
                      <Text fontSize={{ base: 'sm', md: 'md' }}>? Peut-être</Text>
                    </Radio>
                    <Radio value="pending" colorScheme="gray">
                      <Text fontSize={{ base: 'sm', md: 'md' }}>En attente (réinitialiser)</Text>
                    </Radio>
                  </Stack>
                </RadioGroup>
              </FormControl>

              <FormControl>
                <FormLabel fontSize={{ base: 'sm', md: 'md' }}>Méthode de confirmation</FormLabel>
                <RadioGroup value={manualRSVPMethod} onChange={setManualRSVPMethod}>
                  <Stack>
                    <Radio value="phone">
                      <Text fontSize={{ base: 'sm', md: 'md' }}>📞 Par téléphone</Text>
                    </Radio>
                    <Radio value="in_person">
                      <Text fontSize={{ base: 'sm', md: 'md' }}>👤 En personne</Text>
                    </Radio>
                    <Radio value="manual">
                      <Text fontSize={{ base: 'sm', md: 'md' }}>✍️ Saisie manuelle</Text>
                    </Radio>
                  </Stack>
                </RadioGroup>
              </FormControl>

              <FormControl>
                <FormLabel fontSize={{ base: 'sm', md: 'md' }}>Note (optionnel)</FormLabel>
                <Textarea
                  value={manualRSVPNote}
                  onChange={(e) => setManualRSVPNote(e.target.value)}
                  placeholder="Ex: Confirmé par téléphone..."
                  rows={3}
                  fontSize={{ base: 'sm', md: 'md' }}
                />
                <Text fontSize="xs" color="gray.500" mt={1}>
                  Cette note sera visible uniquement par l'équipe
                </Text>
              </FormControl>

              <Alert status="warning" size="sm">
                <AlertIcon />
                <Text fontSize="xs">
                  Cette action enregistrera la réponse sans envoyer d'email.
                </Text>
              </Alert>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onManualRSVPModalClose} size={{ base: 'sm', md: 'md' }}>
              Annuler
            </Button>
            <Button
              colorScheme="blue"
              onClick={handleSubmitManualRSVP}
              isLoading={manualRSVPMutation.isPending}
              size={{ base: 'sm', md: 'md' }}
            >
              Enregistrer
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <SendEmailModal
        isOpen={isSendEmailModalOpen}
        onClose={() => { setSendEmailPick(null); onSendEmailModalClose(); }}
        eventId={parseInt(id!)}
        systemEmails={systemEmailActions}
        initialPick={sendEmailPick}
      />

      {/* Send Final Confirmations Modal */}

      <Modal isOpen={isFinalConfirmModalOpen} onClose={onFinalConfirmModalClose} size={{ base: 'full', md: 'lg' }}>
        <ModalOverlay />
        <ModalContent mx={{ base: 0, md: 4 }}>
          <ModalHeader fontSize={{ base: 'md', md: 'lg' }}>Envoyer les confirmations finales</ModalHeader>
          <ModalCloseButton isDisabled={sendingFinalConfirmations} />
          <ModalBody>
            <VStack spacing={4} align="stretch">
              {!sendingFinalConfirmations ? (
                <>
                  <Alert status="info">
                    <AlertIcon />
                    <Box>
                      <AlertDescription fontSize={{ base: 'xs', md: 'sm' }}>
                        <VStack align="start" spacing={2}>
                          <Text fontWeight="bold">
                            Envoi des confirmations finales avec QR code aux invités présents
                          </Text>
                          <Text fontSize="xs">
                            💡 Seuls les invités n'ayant pas encore reçu de confirmation recevront un email
                          </Text>
                        </VStack>
                      </AlertDescription>
                    </Box>
                  </Alert>

                  <Box p={4} bg="green.50" borderRadius="md">
                    <VStack align="start" spacing={2}>
                      <Text fontWeight="bold" fontSize={{ base: 'xs', md: 'sm' }}>
                        📊 Invités présents : {eventStats.accepted}
                      </Text>
                      <Text fontSize="xs" color="gray.600">
                        Le système enverra uniquement aux invités nécessitant une confirmation
                      </Text>
                    </VStack>
                  </Box>

                  {event.event_date && (
                    <Alert status="info" size="sm">
                      <AlertIcon />
                      <Text fontSize="xs">
                        Date de l'événement : {formatDate(event.event_date)}
                      </Text>
                    </Alert>
                  )}
                </>
              ) : (
                <>
                  <Alert status="info">
                    <AlertIcon />
                    <Text fontSize={{ base: 'xs', md: 'sm' }}>
                      Envoi en cours... Ne fermez pas cette fenêtre.
                    </Text>
                  </Alert>

                  <Box>
                    <Progress
                      size="sm"
                      colorScheme="green"
                      isIndeterminate
                    />
                  </Box>
                </>
              )}
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button
              variant="ghost"
              mr={3}
              onClick={onFinalConfirmModalClose}
              isDisabled={sendingFinalConfirmations}
              size={{ base: 'sm', md: 'md' }}
            >
              Annuler
            </Button>
            <Button
              colorScheme="green"
              onClick={confirmSendFinalConfirmations}
              isLoading={sendingFinalConfirmations}
              loadingText="Envoi..."
              size={{ base: 'sm', md: 'md' }}
            >
              Confirmer
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Send One Invitation Dialog */}
      <AlertDialog
        isOpen={isSendOneDialogOpen}
        leastDestructiveRef={cancelRef}
        onClose={() => {
          onSendOneDialogClose();
          setInviteeToSend(null);
        }}
      >
        <AlertDialogOverlay>
          <AlertDialogContent mx={{ base: 4, md: 0 }}>
            <AlertDialogHeader fontSize={{ base: 'md', md: 'lg' }} fontWeight="bold">
              {inviteeToSend?.status === 'pending'
                ? "Envoyer l'invitation"
                : "Renvoyer l'invitation"}
            </AlertDialogHeader>

            <AlertDialogBody fontSize={{ base: 'sm', md: 'md' }}>
              {inviteeToSend && (
                <>
                  {inviteeToSend.status === 'pending' ? (
                    <>
                      Vous êtes sur le point d'envoyer une invitation à :
                      <br />
                      <strong>{inviteeToSend.name}</strong>
                    </>
                  ) : (
                    <>
                      Vous êtes sur le point de renvoyer une invitation à :
                      <br />
                      <strong>{inviteeToSend.name}</strong>
                    </>
                  )}
                </>
              )}
            </AlertDialogBody>

            <AlertDialogFooter>
              <Button 
                ref={cancelRef} 
                onClick={onSendOneDialogClose}
                size={{ base: 'sm', md: 'md' }}
              >
                Annuler
              </Button>
              <Button
                colorScheme="blue"
                onClick={confirmSendOneInvitation}
                ml={3}
                isLoading={sendInvitationMutation.isPending}
                size={{ base: 'sm', md: 'md' }}
              >
                {inviteeToSend?.status === 'pending'
                  ? "Envoyer"
                  : "Renvoyer"}
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>

      {/* Resend Final Confirmation Dialog */}
      <AlertDialog
        isOpen={isResendConfirmDialogOpen}
        leastDestructiveRef={cancelRef}
        onClose={() => {
          onResendConfirmDialogClose();
          setInviteeToResendConfirmation(null);
        }}
      >
        <AlertDialogOverlay>
          <AlertDialogContent mx={{ base: 4, md: 0 }}>
            <AlertDialogHeader fontSize={{ base: 'md', md: 'lg' }} fontWeight="bold">
              Renvoyer la confirmation finale
            </AlertDialogHeader>

            <AlertDialogBody>
              {inviteeToResendConfirmation && (
                <VStack align="start" spacing={3}>
                  <Text fontSize={{ base: 'sm', md: 'md' }}>
                    Renvoyer la confirmation finale avec QR code à :
                  </Text>

                  <Box p={4} bg="blue.50" borderRadius="md" w="full">
                    <VStack align="start" spacing={1}>
                      <Text fontWeight="bold" fontSize={{ base: 'md', md: 'lg' }}>
                        {inviteeToResendConfirmation.name}
                      </Text>
                      <HStack spacing={2}>
                        <EmailIcon color="blue.500" boxSize={3} />
                        <Text fontSize={{ base: 'xs', md: 'sm' }} color="gray.700">
                          {inviteeToResendConfirmation.email}
                        </Text>
                      </HStack>
                    </VStack>
                  </Box>

                  <Alert status="info" size="sm">
                    <AlertIcon />
                    <VStack align="start" spacing={0}>
                      <Text fontSize="xs" fontWeight="bold">
                        Cette action va :
                      </Text>
                      <Text fontSize="xs">
                        • Envoyer un nouvel email avec QR code
                      </Text>
                      <Text fontSize="xs">
                        • Mettre à jour la date d'envoi
                      </Text>
                    </VStack>
                  </Alert>
                </VStack>
              )}
            </AlertDialogBody>

            <AlertDialogFooter>
              <Button
                ref={cancelRef}
                onClick={() => {
                  onResendConfirmDialogClose();
                  setInviteeToResendConfirmation(null);
                }}
                size={{ base: 'sm', md: 'md' }}
              >
                Annuler
              </Button>
              <Button
                colorScheme="blue"
                onClick={confirmResendConfirmation}
                ml={3}
                isLoading={resendFinalConfirmationMutation.isPending}
                leftIcon={<EmailIcon />}
                size={{ base: 'sm', md: 'md' }}
              >
                Envoyer
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>

    </Box>
  );
};

export default EventDetailPage;